"use client"

import { useState, useCallback, useMemo } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { ShopifyPeriodSelector } from "@/components/shopify/attribution-dashboard/shopify-period-selector"
import { SHOPIFY_PERIOD_OPTIONS } from "@/components/shopify/attribution-dashboard/period-constants"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AlertCircle, Download } from "lucide-react"
import { exportCrossSellToExcel } from "./export-to-excel"
import { TopPairsView } from "./top-pairs-view"
import { FocusProductView } from "./focus-product-view"
import { BundleBuilderView } from "./bundle-builder-view"
import { CompareBundlesView } from "./compare-bundles-view"
import { useCrossSellData } from "./use-cross-sell-data"
import type { CrossSellGroupBy, CrossSellViewMode } from "./types"
import type { ShopifyPeriod } from "@/components/shopify/attribution-dashboard/period-constants"
import type { ProductOption } from "@/types/shopify"
import { useTranslations } from "next-intl"


interface CrossSellContentProps {
  integrationId: string
  dateRange: { month_start_utc: Date; month_end_utc: Date; timezone: string }
  period: ShopifyPeriod
  customRange?: { from: string; to: string } | null
  onPeriodChange: (period: ShopifyPeriod, customRange?: { from: string; to: string }) => void
  minimumOrderTotal: number
  stores: { id: string; shop_domain: string }[]
  selectedStore: string
  onStoreChange: (storeId: string) => void
}

export function CrossSellContent({
  integrationId,
  dateRange,
  period,
  customRange,
  onPeriodChange,
  minimumOrderTotal,
  stores,
  selectedStore,
  onStoreChange,
}: CrossSellContentProps) {
  const tUi = useTranslations("Shopify.ShopifyCrossSellCrossSellContent")

  const [viewMode, setViewMode] = useState<CrossSellViewMode>("pairs")
  // Pairs / Focus group_by toggle. "product" (default) = current behavior.
  // "family" groups co-purchase by family_key. Bundle Builder + Compare
  // Bundles tabs are unaffected.
  const [groupBy, setGroupBy] = useState<CrossSellGroupBy>("product")
  const [focusProducts, setFocusProducts] = useState<ProductOption[]>([])
  // Family-mode focus selection. Independent state from focusProducts so the
  // toggle keeps each side's last selection intact.
  const [focusFamilyKeys, setFocusFamilyKeys] = useState<string[]>([])
  const [bundleSlots, setBundleSlots] = useState<ProductOption[][]>([[], []])
  const [slotLabels, setSlotLabels] = useState<(string | null)[]>([null, null])

  const monthStartUtc = dateRange.month_start_utc.toISOString()
  const monthEndUtc = dateRange.month_end_utc.toISOString()

  const focusProductIds = useMemo(
    () => focusProducts.map((p) => p.product_id),
    [focusProducts],
  )
  const bundleGroups = useMemo(
    () => bundleSlots.map((slot) => slot.map((p) => p.product_id)),
    [bundleSlots],
  )

  const { pairs, pairsByFamily, focus, focusByFamily, bundle, isLoading, error } =
    useCrossSellData({
      integrationId,
      monthStartUtc,
      monthEndUtc,
      mode: viewMode,
      groupBy,
      focusProductIds,
      focusFamilyKeys,
      bundleGroups,
      minimumOrderTotal,
    })

  const periodLabel = useMemo(() => {
    if (period === "custom" && customRange) {
      return `${customRange.from}-to-${customRange.to}`
    }
    return SHOPIFY_PERIOD_OPTIONS.find((o) => o.value === period)?.label.replace(/\s+/g, "-").toLowerCase() ?? period
  }, [period, customRange])

  // Compare Bundles owns its own data flow (useBundlePerformance) and its own
  // export pipeline (none yet) — it doesn't participate in the toolbar Export
  // button or `hasData` here. Family-mode also has no Excel export yet
  // (Phase 4b MVP scope), so toolbar Export is gated to product-mode shapes.
  const hasData =
    viewMode === "pairs"
      ? groupBy === "product" && pairs.length > 0
      : viewMode === "focus"
        ? groupBy === "product" && !!focus
        : viewMode === "bundle"
          ? !!bundle
          : false

  const handleExport = useCallback(() => {
    if (!hasData) return
    exportCrossSellToExcel(
      { mode: viewMode, pairs, focus, bundle, slotLabels },
      periodLabel,
    )
  }, [viewMode, pairs, focus, bundle, slotLabels, periodLabel, hasData])

  // Click on pair → switch to focus mode with that product
  const handlePairClick = useCallback((productId: number) => {
    const pair = pairs.find(
      (p) => p.product_a_id === productId || p.product_b_id === productId,
    )
    if (pair) {
      const isA = pair.product_a_id === productId
      const title = isA ? pair.product_a_title : pair.product_b_title
      const sku = isA ? pair.product_a_sku : pair.product_b_sku
      const totalOrders = isA ? pair.product_a_total_orders : pair.product_b_total_orders
      setFocusProducts([{
        product_id: productId,
        title,
        sku,
        order_count: totalOrders,
        variant_count: 0,
      }])
      setViewMode("focus")
    }
  }, [pairs])

  return (
    <div className="space-y-4">
      {/* Controls bar */}
      <div className="flex flex-wrap items-center gap-3">
        {stores.length > 1 && (
          <Select value={selectedStore} onValueChange={onStoreChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={tUi("selectStore")} />
            </SelectTrigger>
            <SelectContent>
              {stores.map((store) => (
                <SelectItem key={store.id} value={store.id}>
                  {store.shop_domain}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <ShopifyPeriodSelector
          period={period}
          customRange={customRange}
          onPeriodChange={onPeriodChange}
        />
        <div className="ml-auto">
          <Button
            onClick={handleExport}
            variant="outline"
            size="sm"
            disabled={isLoading || !hasData}
          >
            <Download className="mr-2 h-4 w-4" />
            <span><span key={tUi("export")}>{tUi("export")}</span></span>
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs
        value={viewMode}
        onValueChange={(v) => setViewMode(v as CrossSellViewMode)}
      >
        <TabsList>
          <TabsTrigger value="pairs"><span key={tUi("topPairs")}>{tUi("topPairs")}</span></TabsTrigger>
          <TabsTrigger value="focus"><span key={tUi("focusProduct")}>{tUi("focusProduct")}</span></TabsTrigger>
          <TabsTrigger value="bundle"><span key={tUi("bundleBuilder")}>{tUi("bundleBuilder")}</span></TabsTrigger>
          <TabsTrigger value="compare"><span key={tUi("compareBundles")}>{tUi("compareBundles")}</span></TabsTrigger>
        </TabsList>

        <TabsContent value="pairs" className="mt-4 space-y-3">
          <GroupByToggle value={groupBy} onChange={setGroupBy} />
          <TopPairsView
            pairs={pairs}
            pairsByFamily={pairsByFamily}
            groupBy={groupBy}
            isLoading={isLoading}
            error={error}
            onPairClick={handlePairClick}
            integrationId={integrationId}
            monthStartUtc={monthStartUtc}
            monthEndUtc={monthEndUtc}
          />
        </TabsContent>

        <TabsContent value="focus" className="mt-4 space-y-3">
          <GroupByToggle value={groupBy} onChange={setGroupBy} />
          <FocusProductView
            integrationId={integrationId}
            monthStartUtc={monthStartUtc}
            monthEndUtc={monthEndUtc}
            minimumOrderTotal={minimumOrderTotal}
            groupBy={groupBy}
            selectedProducts={focusProducts}
            onProductsChange={setFocusProducts}
            focusFamilyKeys={focusFamilyKeys}
            onFocusFamilyKeysChange={setFocusFamilyKeys}
            focus={focus}
            focusByFamily={focusByFamily}
            isLoading={isLoading}
            error={error}
          />
        </TabsContent>

        <TabsContent value="bundle" className="mt-4">
          <BundleBuilderView
            integrationId={integrationId}
            monthStartUtc={monthStartUtc}
            monthEndUtc={monthEndUtc}
            minimumOrderTotal={minimumOrderTotal}
            slots={bundleSlots}
            onSlotsChange={setBundleSlots}
            slotLabels={slotLabels}
            onSlotLabelsChange={setSlotLabels}
            bundle={bundle}
            isLoading={isLoading}
            error={error}
          />
        </TabsContent>

        <TabsContent value="compare" className="mt-4">
          <CompareBundlesView
            integrationId={integrationId}
            monthStartUtc={monthStartUtc}
            monthEndUtc={monthEndUtc}
            minimumOrderTotal={minimumOrderTotal}
            timezone={dateRange.timezone}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Group_by toggle (Pairs / Focus only)
// ---------------------------------------------------------------------------

function GroupByToggle({
  value,
  onChange,
}: {
  value: CrossSellGroupBy
  onChange: (v: CrossSellGroupBy) => void
}) {
  const tUi = useTranslations("Shopify.ShopifyCrossSellCrossSellContent")

  return (
    <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
      <Switch
        checked={value === "family"}
        onCheckedChange={(c) => onChange(c ? "family" : "product")}
      />
      <span><span key={tUi("groupByProductFamily")}>{tUi("groupByProductFamily")}</span></span>
    </label>
  )
}
